import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, CircularProgress, Typography, Button, Fade, Zoom } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { PlayArrow, SkipNext, VolumeUp } from '@mui/icons-material';

const pulseAnimation = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

const slideIn = keyframes`
  0% { transform: translateY(20px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

const PlayerContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  aspectRatio: '16/9',
  borderRadius: 16,
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: '#000',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
  },
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 3,
  color: 'white',
  padding: 4,
  animation: `${slideIn} 0.5s ease-out`,
}));

const IframeContainer = styled('div')({
  width: '100%',
  height: '100%',
  position: 'relative',
  '& iframe': {
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: '16px',
    transition: 'opacity 0.3s ease',
  },
});

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
  borderRadius: 16,
}));

const ErrorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
  color: 'white',
  padding: 4,
  textAlign: 'center',
  animation: `${slideIn} 0.5s ease-out`,
}));

const PulsingIcon = styled(PlayArrow)(({ theme }) => ({
  fontSize: 48,
  animation: `${pulseAnimation} 2s infinite`,
  marginBottom: 16,
}));

const VideoPlayer = ({ videoId, onEnd, onStart, onError, isPlaying, onPlayingChange, videoDuration, shouldEnterFullscreen, isAutoplayLoading = false }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);


  const [autoplayCountdown, setAutoplayCountdown] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const iframeRef = useRef(null);
  const loadTimeoutRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const playbackTimerRef = useRef(null);
  const autoplayTimerRef = useRef(null);
  const hasStartedRef = useRef(false);
  const startTimeRef = useRef(null);
  const fullscreenAttemptedRef = useRef(false);
  const playerRef = useRef(null);
  const playerReadyRef = useRef(false);

  // Optimized YouTube embed URL for smooth playback
  const embedUrl = videoId 
    ? `https://www.youtube.com/embed/${videoId}?autoplay=1&controls=1&modestbranding=1&rel=0&showinfo=0&fs=1&hl=en&playsinline=1&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`
    : '';

  // Parse YouTube duration format (PT7M21S) to seconds
  const parseDurationToSeconds = useCallback((duration) => {
    if (!duration) {
      console.log('No duration provided, using 5-minute default');
      return 300; // Default to 5 minutes if no duration
    }
    
    console.log('Parsing duration:', duration);
    
    // Handle different duration formats
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) {
      console.log('Duration format not recognized, using default');
      return 300;
    }
    
    const hours = parseInt(match[1] || 0);
    const minutes = parseInt(match[2] || 0);
    const seconds = parseInt(match[3] || 0);
    
    const totalSeconds = hours * 3600 + minutes * 60 + seconds;
    console.log(`Parsed duration: ${hours}h ${minutes}m ${seconds}s = ${totalSeconds}s total`);
    
    // Sanity check: if duration seems invalid, use reasonable defaults
    if (totalSeconds < 10) {
      console.log('Duration too short, using 3-minute default');
      return 180; // 3 minutes for very short videos
    }
    if (totalSeconds > 3600) {
      console.log('Duration very long, using 20-minute cap');
      return 1200; // Cap at 20 minutes for very long videos
    }
    
    return totalSeconds;
  }, []);

  // Start playback progress tracking with robust timestamp-based auto-advance
  const startPlaybackTracking = useCallback(() => {
    if (!videoDuration) {
      console.log('No video duration provided, using fallback timing');
      // Fallback: try to advance after 8 minutes if no duration
      playbackTimerRef.current = setTimeout(() => {
        if (onEnd) {
          console.log(`Video ${videoId} fallback timeout - auto-advancing`);
          onEnd(isFullscreen);
        }
      }, 8 * 60 * 1000); // 8 minutes fallback
      return;
    }
    
    const durationInSeconds = parseDurationToSeconds(videoDuration);
    const startTime = Date.now();
    startTimeRef.current = startTime;
    
    console.log(`Starting playback tracking for ${videoId}: ${durationInSeconds}s duration`);
    
    // Update progress and check for completion every second
    playbackTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000;
      
      // Auto-advance when elapsed time exceeds video duration + 10 second buffer
      const timeWithBuffer = durationInSeconds + 10;
      
      if (elapsed >= timeWithBuffer) {
        console.log(`Video ${videoId} auto-advancing: elapsed ${elapsed.toFixed(1)}s >= ${timeWithBuffer}s (duration + 10s buffer)`);
        clearInterval(playbackTimerRef.current);
        if (onEnd) {
          onEnd(isFullscreen);
        }
        return;
      }
      
      // Log progress for debugging
      if (elapsed % 30 === 0) { // Every 30 seconds
        console.log(`Video ${videoId} progress: ${elapsed.toFixed(0)}s / ${durationInSeconds}s`);
      }
    }, 1000);
  }, [videoDuration, parseDurationToSeconds, onEnd, videoId, isFullscreen]);

  // Handle video error - defined early to avoid dependency issues
  const handleVideoError = useCallback(() => {
    if (!hasError && !hasStartedRef.current) {
      console.log('Video error for:', videoId);
      setHasError(true);
      setIsLoading(false);
      
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      
      // Auto-skip to next video after 2 seconds
      setTimeout(() => {
        if (onError) {
          onError(videoId);
        }
      }, 2000);
    }
  }, [hasError, videoId, onError]);

  // YouTube Player API setup - temporarily disabled for debugging
  // useEffect(() => {
  //   // Load YouTube API if not already loaded
  //   if (!window.YT) {
  //     const script = document.createElement('script');
  //     script.src = 'https://www.youtube.com/iframe_api';
  //     document.body.appendChild(script);
  //     
  //     window.onYouTubeIframeAPIReady = () => {
  //       console.log('YouTube API loaded');
  //     };
  //   }
  // }, []);

  // Initialize YouTube player when iframe loads
  const initializeYouTubePlayer = useCallback(() => {
    if (!window.YT || !window.YT.Player || !iframeRef.current || !videoId) return;
    
    try {
      // Destroy existing player
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        playerRef.current.destroy();
      }
      
      // Create a unique ID for this player instance
      const playerId = `youtube-player-${videoId}`;
      
      // Set the iframe ID for API access
      if (iframeRef.current) {
        iframeRef.current.id = playerId;
      }
      
      playerRef.current = new window.YT.Player(playerId, {
        events: {
          onReady: (event) => {
            console.log('YouTube player ready for video:', videoId);
            playerReadyRef.current = true;
            
            // Clear timer-based fallback since we have API control
            if (playbackTimerRef.current) {
              clearInterval(playbackTimerRef.current);
              clearTimeout(playbackTimerRef.current);
            }
          },
          onStateChange: (event) => {
            const state = event.data;
            console.log('YouTube player state changed:', state, 'for video:', videoId);
            
            if (state === window.YT.PlayerState.PLAYING) {
              if (!hasStartedRef.current) {
                console.log('Video started playing via API:', videoId);
                hasStartedRef.current = true;
                setIsLoading(false);
                setIsVideoReady(true);
                
                if (onStart) {
                  onStart({ title: 'David Dobrik Vlog' });
                }
              }
            } else if (state === window.YT.PlayerState.ENDED) {
              console.log('Video ended via API:', videoId);
              if (onEnd) {
                onEnd(isFullscreen);
              }
            }
          },
          onError: (event) => {
            console.log('YouTube player error:', event.data, 'for video:', videoId);
            handleVideoError();
          }
        }
      });
    } catch (error) {
      console.log('Failed to initialize YouTube player:', error);
      // Fall back to timer-based detection
      startPlaybackTracking();
    }
  }, [videoId, isFullscreen, onStart, onEnd, handleVideoError, startPlaybackTracking]);

  // Fullscreen event handler
  const handleFullscreenChange = useCallback(() => {
    const isCurrentlyFullscreen = !!document.fullscreenElement;
    setIsFullscreen(isCurrentlyFullscreen);
    console.log('Fullscreen state changed:', isCurrentlyFullscreen ? 'entered' : 'exited');
  }, []);

  // Setup fullscreen event listener
  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [handleFullscreenChange]);

  // Enter fullscreen when shouldEnterFullscreen is true and video is ready
  useEffect(() => {
    if (shouldEnterFullscreen && isVideoReady && iframeRef.current && !fullscreenAttemptedRef.current) {
      fullscreenAttemptedRef.current = true;
      console.log('Attempting to enter fullscreen for new video');
      
      // Try to enter fullscreen on the iframe
      const iframe = iframeRef.current;
      const enterFullscreen = iframe.requestFullscreen || 
                              iframe.webkitRequestFullscreen || 
                              iframe.mozRequestFullScreen || 
                              iframe.msRequestFullscreen;
      
      if (enterFullscreen) {
        enterFullscreen.call(iframe).catch(error => {
          console.log('Could not enter fullscreen:', error);
        });
      }
    }
  }, [shouldEnterFullscreen, isVideoReady]);

  // Reset states when video changes
  useEffect(() => {
    if (!videoId) return;

    // Reset all states for new video
    setIsLoading(true);
    setHasError(false);
    setIsVideoReady(false);
    setLoadProgress(0);
    hasStartedRef.current = false;
    fullscreenAttemptedRef.current = false;
    playerReadyRef.current = false;

    // Clear any existing timers
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
    }
    
    // Cleanup YouTube player
    if (playerRef.current && typeof playerRef.current.destroy === 'function') {
      try {
        playerRef.current.destroy();
        playerRef.current = null;
      } catch (error) {
        console.log('Error destroying YouTube player:', error);
      }
    }

    // Start progress animation
    let progress = 0;
    progressIntervalRef.current = setInterval(() => {
      progress += Math.random() * 15 + 5;
      if (progress >= 90) {
        clearInterval(progressIntervalRef.current);
        setLoadProgress(90);
      } else {
        setLoadProgress(progress);
      }
    }, 200);

    // Set a longer timeout for video loading (15 seconds for debugging)
    loadTimeoutRef.current = setTimeout(() => {
      if (isLoading && !hasStartedRef.current) {
        console.log('Video load timeout, trying next:', videoId);
        handleVideoError();
      }
    }, 15000);

    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
      }
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
      }
    };
  }, [videoId]);

  // Handle autoplay countdown
  useEffect(() => {
    if (isAutoplayLoading) {
      let countdown = 3;
      setAutoplayCountdown(countdown);
      
      autoplayTimerRef.current = setInterval(() => {
        countdown--;
        setAutoplayCountdown(countdown);
        
        if (countdown <= 0) {
          clearInterval(autoplayTimerRef.current);
          setAutoplayCountdown(null);
        }
      }, 1000);
    } else {
      setAutoplayCountdown(null);
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
      }
    }

    return () => {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
      }
    };
  }, [isAutoplayLoading]);

  // Handle iframe load event
  const handleIframeLoad = useCallback(() => {
    console.log('Iframe loaded for video:', videoId);
    setLoadProgress(100);
    
    // Clear loading progress animation
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    // For now, use the reliable iframe fallback approach
    setTimeout(() => {
      console.log('Using iframe fallback approach for reliable playback');
      if (!hasStartedRef.current && !hasError) {
        setIsLoading(false);
        setIsVideoReady(true);
        hasStartedRef.current = true;
        
        if (onStart) {
          onStart({ title: 'David Dobrik Vlog' });
        }
        
        startPlaybackTracking();
      }
      
      // Clear the timeout since iframe loaded
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    }, 1000); // Faster loading for better UX
  }, [videoId, hasError, onStart, startPlaybackTracking]);

  // No video ID
  if (!videoId) {
    return (
      <PlayerContainer>
        <LoadingContainer>
          <Zoom in timeout={500}>
            <PulsingIcon sx={{ color: '#FF0050' }} />
          </Zoom>
          <Typography variant="h5" color="white" fontWeight={800}>
            LOADING EPIC CONTENT
          </Typography>
          <Typography variant="body1" color="rgba(255,255,255,0.8)">
            Getting ready for some David Dobrik chaos!
          </Typography>
        </LoadingContainer>
      </PlayerContainer>
    );
  }

  // Error state
  if (hasError) {
    return (
      <PlayerContainer>
        <ErrorContainer>
          <Zoom in timeout={300}>
            <SkipNext sx={{ fontSize: 48, color: '#FF0050', mb: 2 }} />
          </Zoom>
          <Typography variant="h6" color="white" fontWeight={700}>
            Oops! Video Not Available
          </Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.8)" sx={{ mb: 2 }}>
            Don't worry, we're skipping to another amazing vlog!
          </Typography>
          <Box sx={{ 
            width: '100%', 
            height: 4, 
            bgcolor: 'rgba(255,255,255,0.2)', 
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <Box sx={{ 
              width: '100%', 
              height: '100%', 
              bgcolor: '#FF0050',
              animation: 'pulse 1s infinite'
            }} />
          </Box>
        </ErrorContainer>
      </PlayerContainer>
    );
  }

  return (
    <PlayerContainer>
      {/* Loading Overlay */}
      <Fade in={isLoading} timeout={300}>
        <LoadingOverlay style={{ display: isLoading ? 'flex' : 'none' }}>
          <LoadingContainer>
            <Zoom in={isLoading} timeout={500}>
              <Box position="relative">
                <CircularProgress 
                  size={80} 
                  thickness={4}
                  sx={{ color: '#FF0050' }}
                  variant="determinate"
                  value={loadProgress}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <VolumeUp sx={{ color: 'white', fontSize: 32 }} />
                </Box>
              </Box>
            </Zoom>
            <Typography variant="h6" color="white" fontWeight={700}>
              Loading Epic Vlog...
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.8)">
              {loadProgress < 30 ? 'Connecting to YouTube...' :
               loadProgress < 60 ? 'Loading video data...' :
               loadProgress < 90 ? 'Almost ready...' :
               'Starting playback...'}
            </Typography>
            
            {/* Progress Bar */}
            <Box sx={{ 
              width: 200, 
              height: 6, 
              bgcolor: 'rgba(255,255,255,0.2)', 
              borderRadius: 3,
              overflow: 'hidden',
              mt: 1
            }}>
              <Box sx={{ 
                width: `${loadProgress}%`, 
                height: '100%', 
                bgcolor: '#FF0050',
                borderRadius: 3,
                transition: 'width 0.3s ease'
              }} />
            </Box>
          </LoadingContainer>
        </LoadingOverlay>
      </Fade>

      {/* Video iframe */}
      <IframeContainer>
        <iframe
          ref={iframeRef}
          src={embedUrl}
          title="David Dobrik YouTube Video Player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          onLoad={handleIframeLoad}
          onError={handleVideoError}
          style={{ 
            opacity: isVideoReady ? 1 : 0,
            transition: 'opacity 0.5s ease'
          }}
        />
      </IframeContainer>
      


      {/* Autoplay Loading Overlay */}
      {isAutoplayLoading && (
        <LoadingOverlay>
          <Fade in timeout={300}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 3,
              color: 'white',
              textAlign: 'center'
            }}>
              <CircularProgress 
                size={60} 
                sx={{ 
                  color: '#FF0050',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  }
                }} 
              />
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  Loading Next Vlog...
                </Typography>
                {autoplayCountdown !== null && (
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Starting in {autoplayCountdown} second{autoplayCountdown !== 1 ? 's' : ''}
                  </Typography>
                )}
              </Box>
            </Box>
          </Fade>
        </LoadingOverlay>
      )}
    </PlayerContainer>
  );
};

export default VideoPlayer;